import { db } from '@/utils/Firebase'

export function hasFullAccess(account) {
    if (!account) return false
    return account.overrides?.fullAccess === true
        || account?.stripeAccount?.currentSubscription?.adminPass === true
}

export function skipsBilling(account) {
    if (!account) return false
    return hasFullAccess(account) || !!account.overrides?.priceId
}

async function sumCompanyActiveStudents(companyId) {
    if (!companyId) return 0
    const coachesRef = await db.collection('users')
        .where('coachSubscription.companyId', '==', companyId).get()
    let total = 0
    coachesRef.docs.forEach(coach => total += coach.data()?.activeStudents || 0)
    return total
}

async function countCoachStudents(coachId) {
    if (!coachId) return 0
    const studentsRef = await db.collection('users')
        .where('studentSubscription.coachId', '==', coachId).get()
    return studentsRef.size
}

async function countCompanyStudents(companyId) {
    if (!companyId) return 0
    const coachesRef = await db.collection('users')
        .where('coachSubscription.companyId', '==', companyId).get()
    const counts = await Promise.all(coachesRef.docs.map(coach => countCoachStudents(coach.id)))
    return counts.reduce((total, count) => total + count, 0)
}

export async function getCoachClientsLimit(coachId) {
    const empty = { priceId: null, hasPlan: false, maxClients: 0, activeStudents: 0, canInvite: false, reason: 'no-plan' }
    if (!coachId) return empty

    const coachDoc = await db.collection('users').doc(coachId).get()
    const coachData = coachDoc.data()
    if (!coachData) return empty

    let priceId = coachData?.overrides?.priceId
        || coachData?.stripeAccount?.currentSubscription?.priceId
        || null
    let customMaxClients = coachData?.overrides?.customMaxClients
        || coachData?.stripeAccount?.currentSubscription?.customMaxClients
        || null
    let activeStudents = coachData?.activeStudents || 0
    let fullAccess = hasFullAccess(coachData)
    let companyId = null

    if (coachData.accountType === 'company') {
        companyId = coachId
        activeStudents = await sumCompanyActiveStudents(coachId)
    } else if (coachData?.coachSubscription?.companyId || coachData.accountType === 'coach_company') {
        companyId = coachData?.coachSubscription?.companyId || null
        const companyDoc = companyId ? await db.collection('users').doc(companyId).get() : null
        if (!companyDoc || !companyDoc.exists) {
            return fullAccess
                ? { priceId: null, hasPlan: true, maxClients: Infinity, activeStudents, canInvite: true, reason: null }
                : { ...empty, activeStudents, reason: 'no-company' }
        }
        const companyData = companyDoc.data() || {}
        priceId = companyData?.overrides?.priceId
            || companyData?.stripeAccount?.currentSubscription?.priceId
            || null
        customMaxClients = companyData?.overrides?.customMaxClients
            || companyData?.stripeAccount?.currentSubscription?.customMaxClients
            || null
        activeStudents = await sumCompanyActiveStudents(companyId)
        fullAccess = fullAccess || hasFullAccess(companyData)
    }

    if (fullAccess) return { priceId, hasPlan: true, maxClients: Infinity, activeStudents, canInvite: true, reason: null }

    if (!priceId) return { ...empty, activeStudents }

    const plansRef = await db.collection('plans').where('priceId', '==', priceId).get()
    if (plansRef.docs.length === 0) return { ...empty, priceId, activeStudents }

    const plan = plansRef.docs[0].data()
    const maxClients = customMaxClients || plan.maxClients || 0
    const students = companyId
        ? await countCompanyStudents(companyId)
        : await countCoachStudents(coachId)
    const canInvite = students < maxClients

    return {
        priceId,
        hasPlan: true,
        maxClients,
        activeStudents: students,
        canInvite,
        reason: canInvite ? null : 'limit',
    }
}
